import { v4 as uuidv4 } from 'uuid';
import * as THREE from 'three';
import { BaseTool } from '../BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import { View, Element } from '../../core/types';

export interface CameraElement extends Element {
    category: 'Views';
    geometryData: {
        position: [number, number, number]; // Eye
        target: [number, number, number];   // Target
        fov: number;
        viewId: string;
    };
}

export class CameraTool extends BaseTool {
    id = 'VIEW_CAMERA';
    name = 'Camera';
    cursor = 'crosshair';

    private startPoint: THREE.Vector3 | null = null;
    private currentPoint: THREE.Vector3 | null = null;
    // BIM camera usually has a fixed height offset relative to the level, but let's assume raw points for now
    private eyeHeight = 1.75; // 1.75m default eye height

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const point = this.getIntersection(event);
        if (!point) return;

        // Don't snap strongly for camera, maybe just to level?
        const snapped = this.snapToGrid(point);
        // Force Z to current active level or ground? 
        // For now, assume clicked point is on ground/level, and add eye height.
        // Actually, if we are in Plan View, Z is constant. 
        // Let's assume we are clicking on a plan, so we add height.

        const eyePoint = snapped.clone().add(new THREE.Vector3(0, 0, this.eyeHeight));

        if (!this.startPoint) {
            this.startPoint = eyePoint;
            this.currentPoint = eyePoint; // Init
            this.updatePreview();
        } else {
            // Second click is Target point. Target height? usually same as eye or clicked object.
            // Let's assume target is at eye height for level view, or actual picked point.
            // Simplified: Target is also at eye height (level look).
            const targetPoint = snapped.clone().add(new THREE.Vector3(0, 0, this.eyeHeight));

            this.createCameraView(this.startPoint, targetPoint);

            this.startPoint = null;
            this.currentPoint = null;
            this.context.appStore.setToolPreview(null);
            this.context.appStore.setActiveTool('select');
        }
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context || !this.startPoint) return;
        const point = this.getIntersection(event);
        if (point) {
            const snapped = this.snapToGrid(point);
            const target = snapped.clone().add(new THREE.Vector3(0, 0, this.eyeHeight));
            this.currentPoint = target;
            this.updatePreview();
        }
    }

    private updatePreview() {
        if (!this.context || !this.startPoint || !this.currentPoint) return;

        // Draw camera cone or just a line
        this.context.appStore.setToolPreview({
            type: 'camera',
            position: this.startPoint,
            target: this.currentPoint,
            fov: 50
        });
    }

    private createCameraView(eye: THREE.Vector3, target: THREE.Vector3) {
        if (!this.context) return;

        const viewId = uuidv4();
        const elementId = uuidv4();
        const name = `Camera ${this.context.bimStore.project.views.size + 1}`;

        // Create View
        const newView: View = {
            id: viewId,
            name: name,
            type: '3D',
            discipline: 'Architectural',
            scale: 1, // Perspective doesn't really have scale
            detailLevel: 'Fine',
            visibility: {},
            filters: [],
            camera: {
                position: [eye.x, eye.y, eye.z],
                target: [target.x, target.y, target.z],
                projection: 'Perspective'
            },
            // Clipping planes/Section Box?
        };

        // Create Camera Element (Visual representation in Plan)
        const camEl: CameraElement = {
            id: elementId,
            category: 'Views',
            type: 'Camera Marker',
            parameters: {
                'View Name': name,
                'View Id': viewId,
                'Eye Elevation': eye.z,
                'Target Elevation': target.z
            },
            constraints: [],
            geometryData: {
                position: [eye.x, eye.y, eye.z],
                target: [target.x, target.y, target.z],
                fov: 50,
                viewId: viewId
            }
        };

        this.context.bimStore.addView(newView);
        this.context.bimStore.addElement(camEl);

        console.log(`Created Camera View: ${name}`);
        // Optionally switch to it
        // this.context.bimStore.setActiveView(viewId);
    }

    onPointerUp(): void { }
}
